// ── App Activity Log ─────────────────────────────────────────────────────
// Who did what, written inside the same transaction / batch as the action
// itself (so the line and the change always land together, and a delete
// still leaves a trace). The Activity Log page reads `activityLog`.

import { collection, doc, serverTimestamp, getDocs, query, where, limit, writeBatch, type DocumentReference, type Firestore } from 'firebase/firestore';
import type { ActivityEntry } from '../types';

type Writer = { set: (ref: DocumentReference, data: Record<string, unknown>) => unknown };
type Actor  = { id: string; name?: string } | null | undefined;

export const actorName = (user: Actor) => user?.name?.trim() || 'Unknown user';

/** Fields stamped on a record when it is created. */
export const createdStamp = (user: Actor) => ({
  createdBy: user?.id ?? '', createdByName: actorName(user),
});

/** Fields stamped on a record every time it is edited. */
export const updatedStamp = (user: Actor) => ({
  updatedBy: user?.id ?? '', updatedByName: actorName(user),
});

export type ActivityInput = Omit<ActivityEntry, 'id' | 'businessId' | 'userId' | 'userName' | 'at' | 'createdAt'>;

export function logActivity(w: Writer, db: Firestore, businessId: string, user: Actor, entry: ActivityInput) {
  const data: Record<string, unknown> = {
    ...entry, businessId,
    userId: user?.id ?? '', userName: actorName(user),
    at: new Date().toISOString(), createdAt: serverTimestamp(),
  };
  Object.keys(data).forEach(k => data[k] === undefined && delete data[k]);
  w.set(doc(collection(db, 'activityLog')), data);
}

/** Name of who added a record, for list columns / CSV ('' when unknown). */
export function addedByName(
  record: { createdBy?: string; createdByName?: string },
  me: { id: string; name: string } | null | undefined,
): string {
  return record.createdByName || (record.createdBy && me && record.createdBy === me.id ? me.name : '');
}

// ── Keep only the last 3 months ──────────────────────────────────────────
// The log is kept for the same window the page's "3 Months" tab shows:
// from the 1st of the month two months back. Anything older is deleted.

/** Start of what's kept, as ISO time (local midnight on the 1st, two months back). */
export function activityKeepFrom(now = new Date()): string {
  return new Date(now.getFullYear(), now.getMonth() - 2, 1).toISOString();
}

const PRUNE_KEY = 'activity-log-pruned';

/** Deletes log lines older than the kept window. Runs at most once a day per
 *  browser (owner only, called from AppLayout). One field range query, so no index. */
export async function pruneActivityLog(db: Firestore): Promise<void> {
  const today = new Date().toDateString();
  try { if (localStorage.getItem(PRUNE_KEY) === today) return; } catch { /* storage blocked: just run */ }
  const cutoff = activityKeepFrom();
  for (;;) {
    const snap = await getDocs(query(collection(db, 'activityLog'), where('at', '<', cutoff), limit(400)));
    if (snap.empty) break;
    const batch = writeBatch(db);
    snap.docs.forEach(d => batch.delete(d.ref));
    await batch.commit();
    if (snap.size < 400) break;
  }
  try { localStorage.setItem(PRUNE_KEY, today); } catch { /* ignore */ }
}
